const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function getToken() { return localStorage.getItem('token'); }

async function request(method, path, body = null, extraHeaders = {}) {
  const headers = { 'Content-Type': 'application/json', ...extraHeaders };
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${BASE}${path}`, {
    method, headers, body: body ? JSON.stringify(body) : null,
  });
  if (res.status === 401) {
    // #107: «Sesión revocada» (cambio de contraseña, MFA, «Cerrar sesiones») se explica en el login; los demás 401, como siempre
    const err = await res.json().catch(() => ({}));
    if (err.detail === 'Sesión revocada') { try { sessionStorage.setItem('logout_reason', 'revoked'); } catch (e) { /* sin storage */ } }
    localStorage.removeItem('token'); window.location.href = '/'; return;
  }
  if (!res.ok) { const err = await res.json().catch(() => ({})); const e = new Error(err.detail || 'Error en la API'); e.status = res.status; throw e; }
  if (res.status === 204) return null;
  const data = await res.json();
  // #107: un cambio propio que revoca las sesiones (contraseña, MFA, códigos, email) trae un token nuevo para
  // quien lo hizo: reemplaza el guardado y la sesión sigue, sin pasar por el login.
  if (data && typeof data === 'object' && data.access_token && !extraHeaders.Authorization) {
    localStorage.setItem('token', data.access_token);
  }
  return data;
}

export const api = {
  login: async (email, password) => {
    const form = new URLSearchParams();
    form.append('username', email); form.append('password', password);
    const res = await fetch(`${BASE}/api/auth/login`, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: form });
    if (!res.ok) throw new Error('Credenciales incorrectas');
    return res.json();
  },
  loginMfa: async (mfa_token, code) => {
    const res = await fetch(`${BASE}/api/auth/login/mfa`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mfa_token, code }),
    });
    if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Código incorrecto'); }
    return res.json();
  },
  acceptTerms: async (acceptance_token, acceptances) => {
    const res = await fetch(`${BASE}/api/auth/accept-terms`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${acceptance_token}` },
      body: JSON.stringify({ acceptances }),
    });
    if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Error al aceptar los documentos'); }
    return res.json();
  },
  mfaSetup:   () => request('POST', '/api/auth/mfa/setup'),
  mfaEnable:  (secret, code) => request('POST', '/api/auth/mfa/enable', { secret, code }),
  mfaDisable: (code) => request('POST', '/api/auth/mfa/disable', { code }),
  mfaSetupForced:        (setup_token) => request('POST', '/api/auth/mfa/setup-forced', { setup_token }),
  mfaSetupForcedConfirm: (setup_token, secret, code) => request('POST', '/api/auth/mfa/setup-forced/confirm', { setup_token, secret, code }),
  mfaRecoveryCodesRegenerate: (code) => request('POST', '/api/auth/mfa/recovery-codes/regenerate', { code }),
  mfaRecoveryCodesStatus:     () => request('GET', '/api/auth/mfa/recovery-codes/status'),
  me: () => request('GET', '/api/auth/me'),
  updateMe: (data) => request('PATCH', '/api/auth/me', data),

  summary:              () => request('GET', '/api/dashboard/summary'),
  activity:             () => request('GET', '/api/dashboard/activity'),
  storageUpdated:       () => request('GET', '/api/dashboard/storage-updated'),
  storageSummary:       () => request('GET', '/api/dashboard/storage-summary'),
  getServersOverview:   () => request('GET', '/api/dashboard/servers-overview'),
  onlineUsers:          () => request('GET', '/api/auth/online'),

  getClients:   (includeInternal = false, includeInactive = false) => {
    const qs = new URLSearchParams();
    if (includeInternal) qs.set('include_internal', 'true');
    if (includeInactive) qs.set('include_inactive', 'true');
    const q = qs.toString();
    return request('GET', `/api/clients/${q ? '?' + q : ''}`);
  },
  getClient:    (id) => request('GET', `/api/clients/${id}`),
  createClient: (data) => request('POST', '/api/clients/', data),
  updateClient: (id, data) => request('PUT', `/api/clients/${id}`, data),
  deleteClient:      (id) => request('DELETE', `/api/clients/${id}`),
  hardDeleteClient:  (id, confirmName, deleteWasabiData) =>
    request('DELETE', `/api/clients/${id}/hard`, { confirm_name: confirmName, delete_wasabi_data: deleteWasabiData }),
  getClientWasabiSize: (id) => request('GET', `/api/clients/${id}/wasabi-size`),
  reactivateClient:  (id) => request('POST', `/api/clients/${id}/reactivate`),
  reprovisionClient: (id) => request('POST',  `/api/clients/${id}/reprovision`),
  enableCovertCopy:  (clientId, region, confirm, covertBucket) =>
      request('POST', `/api/clients/${clientId}/covert-copy`, { region, confirm, covert_bucket: covertBucket || undefined }),
  disableCovertCopy: (clientId) =>
      request('DELETE', `/api/clients/${clientId}/covert-copy`),
  getToken:     (id) => request('POST', `/api/clients/${id}/token`),
  getPlans:     () => request('GET', '/api/clients/plans/all'),
  createPlan:   (data) => request('POST', '/api/clients/plans', data),
  updatePlan:   (id, data) => request('PUT', `/api/clients/plans/${id}`, data),

  getServers:     (clientId, includeInactive = false) => {
    const qs = new URLSearchParams({ client_id: clientId });
    if (includeInactive) qs.set('include_inactive', 'true');
    return request('GET', `/api/servers/?${qs}`);
  },
  getServerHeartbeats: (clientId) =>
    request('GET', clientId ? `/api/servers/heartbeats?client_id=${clientId}` : '/api/servers/heartbeats'),
  createServer:   (data) => request('POST', '/api/servers/', data),
  deleteServer:   (id) => request('DELETE', `/api/servers/${id}`),
  hardDeleteServer: (id, confirmHostname, deleteWasabiData) =>
    request('DELETE', `/api/servers/${id}/hard`, { confirm_hostname: confirmHostname, delete_wasabi_data: deleteWasabiData }),
  getServerWasabiSize: (id) => request('GET', `/api/servers/${id}/wasabi-size`),
  transferServer:      (server_id, target_client_id) => request('PATCH', `/api/servers/${server_id}/transfer`, { target_client_id }),
  getServerSnapshots:  (serverId) => request('GET', `/api/servers/${serverId}/snapshots`),
  getServerOnlineStatus:  (id) => request('GET', `/api/servers/${id}/online-status`),
  regenerateServerToken:  (id, confirmHostname) =>
    request('POST', `/api/servers/${id}/regenerate-token`, { confirm_hostname: confirmHostname }),

  getJobs:          (serverId)  => request('GET', `/api/jobs/?server_id=${serverId}`),
  getJobsByClient:  (clientId)  => request('GET', `/api/jobs/?client_id=${clientId}`),  // F-07 FIX
  getRecentRuns: (limit = 20, clientId = null) => request('GET', `/api/jobs/runs/recent?limit=${limit}${clientId ? `&client_id=${clientId}` : ''}`),
  createJob:     (data) => request('POST', '/api/jobs/', data),
  updateJob:     (id, data) => request('PUT', `/api/jobs/${id}`, data),
  deleteJob:     (id) => request('DELETE', `/api/jobs/${id}`),
  triggerJob:    (id) => request('POST',   `/api/jobs/${id}/trigger`),
  // #207 parte 2 — rearmar honeypots (sólo admin) y consultar el resultado del pedido
  rearmHoneypots:       (id) => request('POST', `/api/jobs/${id}/honeypots/rearm`),
  rearmHoneypotsStatus: (id, requestId) => request('GET', `/api/jobs/${id}/honeypots/rearm/${requestId}`),
  listSnapshot:   (server_id, snapshot_id) => request('POST', '/api/restores/list-snapshot', { server_id, snapshot_id }),
  getRestores:    () => request('GET', '/api/restores/'),
  getRestore:     (restore_id) => request('GET', `/api/restores/${restore_id}`),
  getListing:     (listing_id) => request('GET', `/api/restores/listing/${listing_id}`),
  granularRestore:(server_id, snapshot_id, target_path, includes, target_server_id = null) =>
    request('POST', '/api/restores/granular', { server_id, snapshot_id, target_path, includes, target_server_id }),
  getSettings:    () => request('GET',  '/api/settings/'),
  getTaskHeartbeats: () => request('GET', '/api/settings/task-heartbeats'),
  getSystemInfo:  () => request('GET',  '/api/settings/system-info'),
  updateSettings: (data) => request('PUT', '/api/settings/', data),
  testWasabi:     () => request('POST', '/api/settings/test-wasabi'),
  testCloudConfig: (payload) => request('POST', '/api/jobs/test-cloud-config', payload),
  // B03 FIX: función faltante — Settings.js llama a api.testTelegram()
  testTelegram:   () => request('POST', '/api/settings/test-telegram'),
  getJobRuns:    (jobId) => request('GET', `/api/jobs/${jobId}/runs`),
  getGlobalAnomalyThreshold:  ()          => request('GET',    '/api/jobs/anomaly-thresholds/global'),
  updateGlobalAnomalyThreshold: (data)    => request('PATCH',  '/api/jobs/anomaly-thresholds/global', data),
  getJobAnomalyThreshold:     (jobId)     => request('GET',    `/api/jobs/${jobId}/anomaly-threshold`),
  upsertJobAnomalyThreshold:  (jobId, data) => request('PUT',  `/api/jobs/${jobId}/anomaly-threshold`, data),
  deleteJobAnomalyThreshold:  (jobId)     => request('DELETE', `/api/jobs/${jobId}/anomaly-threshold`),

  getLogs: (level) => request('GET', `/api/logs/${level ? '?level=' + level : ''}`),

  getUsers:            () => request('GET', '/api/users/'),
  getCompanyUsers:     () => request('GET', '/api/users/?scope=company'),
  createUser:          (data) => request('POST', '/api/users/', data),
  updateUser:          (id, data) => request('PUT', `/api/users/${id}`, data),
  deleteUser:          (id) => request('DELETE', `/api/users/${id}`),
  hardDeleteUser: async (id) => {
    const token = getToken();
    const res = await fetch(`${BASE}/api/users/${id}/hard`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (res.status === 401) { localStorage.removeItem('token'); window.location.href = '/'; return; }
    if (res.status === 409) {
      const data = await res.json();
      const err = new Error('CONFLICT');
      err.conflictDetail = data.detail;
      throw err;
    }
    if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Error en la API'); }
    return null;
  },
  anonymizeUser:       (id) => request('POST', `/api/users/${id}/anonymize`),
  resetPassword:       (id, password) => request('POST', `/api/users/${id}/reset-password`, { password }),
  changePassword:      (current_password, new_password) => request('POST', '/api/auth/change-password', { current_password, new_password }),
  revokeMySessions:    () => request('POST', '/api/auth/sessions/revoke'),
  revokeUserSessions:  (id) => request('POST', `/api/users/${id}/revoke-sessions`),
  getResellers:        () => request('GET', '/api/users/resellers'),
  getResellerCompanies: (includeInactive = false) => request('GET', `/api/resellers/${includeInactive ? '?include_inactive=true' : ''}`),
  getResellerCompany:  (id) => request('GET', `/api/resellers/${id}`),
  updateResellerPerms: (id, perms) => request('PUT', `/api/users/${id}/reseller-perms`, perms),
  getResellerCeiling:  (clientId) => request('GET', `/api/clients/${clientId}/reseller-ceiling`),
  updateResellerCeiling: (clientId, perms) => request('PUT', `/api/clients/${clientId}/reseller-ceiling`, perms),
  getRecoveryKey:  (client_id, server_id) =>
      request('GET', `/api/clients/${client_id}/recovery-key/${server_id}`),
  getRecoveryKit:  (client_id) =>
      request('GET', `/api/clients/${client_id}/recovery-kit`),
  getAuditLog:     (filters = {}) => {
      const qs = new URLSearchParams();
      if (filters.limit)      qs.set('limit', filters.limit);
      if (filters.action)     qs.set('action', filters.action);
      if (filters.userEmail)  qs.set('user_email', filters.userEmail);
      if (filters.ip)         qs.set('ip', filters.ip);
      if (filters.dateFrom)   qs.set('date_from', filters.dateFrom);
      if (filters.dateTo)     qs.set('date_to', filters.dateTo);
      return request('GET', `/api/dashboard/audit-log?${qs}`);
  },
  getAuditLogActions: () => request('GET', '/api/dashboard/audit-log/actions'),

  getDelegationCheck:        (client_id) =>
      request('GET', `/api/delegations/check?client_id=${client_id}`),
  getClientDelegations:      (client_id) =>
      request('GET', `/api/delegations/client/${client_id}`),
  saveClientDelegation:      (client_id, ops) =>
      request('POST', `/api/delegations/client/${client_id}`, ops),
  revokeDelegation:          (id) =>
      request('DELETE', `/api/delegations/${id}`),
  requestSubdelegation:      (id, notes) =>
      request('POST', `/api/delegations/${id}/request-subdelegation`, { notes }),
  approveSubdelegation:      (id, approved) =>
      request('POST', `/api/delegations/${id}/approve-subdelegation`, { approved }),
  requestOverride:           (data) =>
      request('POST', '/api/delegations/override', data),

  getNotificationPrefs:    (user_id) =>
      request('GET', `/api/users/${user_id}/notification-preferences`),
  getLegalAcceptances:     (userId) =>
      request('GET', `/api/users/${userId}/legal-acceptances`),
  updateNotificationPrefs: (user_id, data) =>
      request('PUT', `/api/users/${user_id}/notification-preferences`, data),
  testSmtp:                () =>
      request('POST', '/api/settings/test-smtp', {}),

  // Wizard de primer arranque
  checkFirstRun: async () => {
    const res = await fetch(`${BASE}/api/auth/first-run`);
    return res.json();
  },
  wizardSetAdminPassword: (token, email, password) =>
    request('POST', '/api/wizard/set-admin-password', { email, password }, { Authorization: `Bearer ${token}` }),
  wizardTestWasabi: (token, data) =>
    request('POST', '/api/wizard/test-wasabi', data, { Authorization: `Bearer ${token}` }),
  wizardSaveWasabi: (token, data) =>
    request('POST', '/api/wizard/save-wasabi', data, { Authorization: `Bearer ${token}` }),
  wizardSetupInternalCompany: (token, data) =>
    request('POST', '/api/wizard/setup-internal-company', data, { Authorization: `Bearer ${token}` }),
  wizardCreatePlan: (token, data) =>
    request('POST', '/api/wizard/create-plan', data, { Authorization: `Bearer ${token}` }),
  wizardSaveTelegram: (token, data) =>
    request('POST', '/api/wizard/save-telegram', data, { Authorization: `Bearer ${token}` }),
  wizardComplete: (token) =>
    request('POST', '/api/wizard/complete', null, { Authorization: `Bearer ${token}` }),

  billingReport: () => {
    const token = localStorage.getItem('token');
    return fetch(`${BASE}/api/dashboard/billing-report`, {
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(res => {
      if (!res.ok) throw new Error('Error generando reporte');
      return res.blob();
    });
  },

  forgotPassword: (email) =>
    request('POST', '/api/auth/forgot-password', { email }),
  resetPasswordWithToken: (token, new_password) =>
    request('POST', '/api/auth/reset-password', { token, new_password }),

  getBranding:            () => request('GET', '/api/settings/branding'),
  getBrandingMe:          () => request('GET', '/api/settings/branding/me'),
  updateResellerBranding: (data) => request('PUT', '/api/settings/reseller-branding', data),
  getAgentReleases: () =>
    request('GET', '/api/agent/admin/releases'),
  promoteAgentRelease: (version) =>
    request('POST', `/api/agent/admin/releases/${version}/promote`),
  deprecateAgentRelease: (version) =>
    request('POST', `/api/agent/admin/releases/${version}/deprecate`),
  updateServerConfig: (serverId, config) =>
    request('PATCH', `/api/agent/admin/servers/${serverId}/update-config`, config),
  updateCrossServerRestore: (clientId, enabled) =>
    request('PATCH', `/api/clients/${clientId}/cross-server-restore`, { enabled }),
  triggerServerUpdate: (serverId, version = null) => {
    const url = version
      ? `/api/agent/admin/servers/${serverId}/trigger-update?version=${encodeURIComponent(version)}`
      : `/api/agent/admin/servers/${serverId}/trigger-update`;
    return request('POST', url);
  },

  uploadAsset: async (assetType, file) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('asset_type', assetType);
    formData.append('file', file);
    const base = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    const res = await fetch(`${base}/api/settings/upload-asset`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(err.detail || 'Error al subir el archivo');
    }
    return res.json();
  },
};

